/**
 * One card and its lifecycle.
 *
 * Three visible states, all CSS: it appears (fade + 8px rise), it flashes its
 * outline when it changes, and it collapses when it leaves. No animation
 * library — those three are exactly what transitions do for free.
 *
 * The flash is keyed on `revision` rather than on the props: validation rebuilds
 * the node object every time (zod fills defaults), so «did this change?» cannot
 * be inferred from identity and has to be told.
 *
 * The `exit` phase is what the surface store's `exiting` bookkeeping is for: a
 * removed node is an ORPHAN under A2UI, still in `components` and merely gone
 * from its region's `children`, so it is drawn here for one more animation
 * frame in the place it just left.
 *
 * `staggerIndex` (§5.5.4) is a plain inline CSS var, not a class or a timer:
 * `Board.tsx` computes each card's position among the cards that arrived in
 * the same batch and caps it at 5, so the entrance cascade never reads a
 * position past that from here.
 */

import { memo, useEffect, useRef, useState, type CSSProperties } from 'react';
import { catalogEntry } from '../catalog/registry';
import UnknownCard from '../catalog/components/UnknownCard';
import ErrorBoundary from '../app/ErrorBoundary';
import { cn } from '../lib/cn';
import type { SurfaceNode } from '../a2ui/surfaceStore';

function BoardItem({
  node,
  focused,
  staggerIndex = 0,
}: {
  node: SurfaceNode;
  focused: boolean;
  staggerIndex?: number;
}) {
  const entry = catalogEntry(node.component);
  const [flash, setFlash] = useState(false);
  const seenRevision = useRef(node.revision);

  useEffect(() => {
    if (seenRevision.current === node.revision) return;
    seenRevision.current = node.revision;
    if (node.phase === 'enter') return; // an entrance is not an update
    setFlash(true);
    const timer = setTimeout(() => setFlash(false), 1200);
    return () => clearTimeout(timer);
  }, [node.revision, node.phase]);

  const Component = entry?.Component ?? null;
  // A known component with no renderer is drawn elsewhere on purpose
  // (`ChipGroup` and its buttons live in the chat strip).
  if (entry && !Component) return null;

  return (
    <div
      className={cn(
        'board__item',
        `board__item--${node.phase}`,
        entry?.wide && 'board__item--wide',
        node.status === 'stale' && 'board__item--stale',
        node.status === 'error' && 'board__item--error',
        flash && 'board__item--flash',
        focused && 'board__item--focused',
      )}
      data-component={node.component}
      data-id={node.id}
      style={node.phase === 'enter' ? ({ '--stagger-i': staggerIndex } as CSSProperties) : undefined}
    >
      {node.status === 'stale' ? <span className="board__stale">устарело</span> : null}
      <ErrorBoundary label={`card:${node.component}`}>
        {Component ? (
          <Component props={node.props as never} />
        ) : (
          <UnknownCard props={node.props} />
        )}
      </ErrorBoundary>
    </div>
  );
}

// The board redraws on every message; a card whose node object did not change
// must not re-render with it.
export default memo(BoardItem);
